import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import * as XLSX from 'xlsx';

dayjs.extend(customParseFormat);

const DATE_FORMATS = [
  'YYYY-MM-DD',
  'DD-MM-YYYY',
  'DD/MM/YYYY',
  'D/M/YYYY',
  'D-M-YYYY',
  'DD.MM.YYYY',
  'YYYY/MM/DD',
  'DD-MMM-YYYY',
  'D-MMM-YYYY',
  'DD MMM YYYY',
  'MMM DD, YYYY',
];

/** Strips everything but letters and digits so headers/names compare loosely. */
const normalize = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const parseText = (value) => String(value ?? '').trim();

/**
 * Excel dates arrive as serial numbers, JS dates or free text depending on how
 * the source app wrote the file. Everything is normalised to YYYY-MM-DD.
 */
const parseDate = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  if (value instanceof Date) {
    return dayjs(value).format('YYYY-MM-DD');
  }
  if (typeof value === 'number') {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (!parsed || !parsed.y) {
      return '';
    }
    return dayjs(new Date(parsed.y, parsed.m - 1, parsed.d)).format(
      'YYYY-MM-DD',
    );
  }
  const text = parseText(value);
  const format = DATE_FORMATS.find((f) => dayjs(text, f, true).isValid());
  if (format) {
    return dayjs(text, format, true).format('YYYY-MM-DD');
  }
  const loose = dayjs(text);
  return loose.isValid() ? loose.format('YYYY-MM-DD') : '';
};

/** Handles currency symbols, thousands separators and bracketed negatives. */
const parseNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }
  let text = parseText(value);
  if (!text) {
    return null;
  }
  const isNegative = /^\(.*\)$/.test(text) || text.includes('-');
  text = text.replace(/[^0-9.]/g, '');
  if (!text || text === '.') {
    return null;
  }
  const number = Number(text);
  if (Number.isNaN(number)) {
    return null;
  }
  return isNegative ? -number : number;
};

const parseMobile = (value) => {
  const digits = parseText(value).replace(/\D/g, '');
  return digits.length > 10 ? digits.slice(-10) : digits;
};

/** Splits "John Smith" into { first: 'John', last: 'Smith' } for sheets with a single name column. */
const splitFullName = (value) => {
  const parts = parseText(value).split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return { first: '', last: '' };
  }
  return { first: parts[0], last: parts.slice(1).join(' ') };
};

const parseByType = (value, type) => {
  switch (type) {
    case 'date':
      return parseDate(value);
    case 'number':
      return parseNumber(value);
    case 'mobile':
      return parseMobile(value);
    default:
      return parseText(value);
  }
};

const CUSTOMER_FIELDS = [
  {
    key: 'start_date',
    label: 'Date',
    type: 'date',
    required: true,
    allowDefault: true,
    aliases: [
      'date',
      'start date',
      'billing start date',
      'opening date',
      'as on date',
      'from date',
    ],
  },
  {
    key: 'first_name',
    label: 'First name',
    type: 'text',
    required: true,
    note: 'A single "Name" column also works — it will be split into first and last name automatically.',
    aliases: [
      'first name',
      'name',
      'customer name',
      'customer',
      'party name',
      'account name',
      'ledger name',
    ],
  },
  {
    key: 'last_name',
    label: 'Last name',
    type: 'text',
    aliases: ['last name', 'surname'],
  },
  {
    key: 'mobile',
    label: 'Mobile number',
    type: 'mobile',
    required: true,
    aliases: [
      'mobile',
      'mobile number',
      'mobile no',
      'phone',
      'phone number',
      'contact',
      'contact number',
      'cell',
    ],
  },
  {
    key: 'email',
    label: 'Email',
    type: 'text',
    aliases: ['email', 'email id', 'mail', 'mail id'],
  },
  {
    key: 'gstn',
    label: 'GST number',
    type: 'text',
    aliases: ['gst', 'gstn', 'gstin', 'gst number', 'gst no'],
  },
  {
    key: 'pan',
    label: 'PAN',
    type: 'text',
    aliases: ['pan', 'pan number', 'pan no'],
  },
  {
    key: 'payment_period',
    label: 'Billing cycle (days)',
    type: 'number',
    required: true,
    allowDefault: true,
    aliases: [
      'billing cycle',
      'billing cycle in days',
      'payment period',
      'credit days',
      'credit period',
      'cycle',
    ],
  },
  {
    key: 'limit',
    label: 'Credit limit',
    type: 'number',
    required: true,
    allowDefault: true,
    aliases: ['limit', 'credit limit', 'max limit', 'credit'],
  },
  {
    key: 'outstanding',
    label: 'Opening balance',
    type: 'number',
    required: true,
    allowDefault: true,
    aliases: [
      'opening balance',
      'outstanding',
      'balance',
      'opening outstanding',
      'closing balance',
      'due',
      'due amount',
    ],
  },
  {
    key: 'pincode',
    label: 'Pincode',
    type: 'text',
    aliases: ['pincode', 'pin code', 'pin', 'zip', 'postal code'],
  },
  {
    key: 'city',
    label: 'City',
    type: 'text',
    aliases: ['city', 'town', 'district'],
  },
  {
    key: 'state',
    label: 'State',
    type: 'text',
    aliases: ['state', 'province'],
  },
  {
    key: 'full_address',
    label: 'Address',
    type: 'text',
    aliases: ['address', 'full address', 'billing address', 'address line 1'],
  },
  {
    key: 'notes',
    label: 'Notes',
    type: 'text',
    aliases: ['notes', 'remarks', 'note', 'comments'],
  },
  {
    key: 'vehicle_no',
    label: 'Vehicle number',
    type: 'text',
    aliases: [
      'vehicle',
      'vehicle number',
      'vehicle no',
      'truck number',
      'registration number',
    ],
  },
];

const STAFF_FIELDS = [
  {
    key: 'first_name',
    label: 'First name',
    type: 'text',
    required: true,
    note: 'A single "Name" column also works — it will be split into first and last name automatically.',
    aliases: [
      'first name',
      'name',
      'staff name',
      'employee name',
      'full name',
    ],
  },
  {
    key: 'last_name',
    label: 'Last name',
    type: 'text',
    aliases: ['last name', 'surname'],
  },
  {
    key: 'mobile',
    label: 'Mobile number',
    type: 'mobile',
    required: true,
    aliases: [
      'mobile',
      'mobile number',
      'mobile no',
      'phone',
      'phone number',
      'contact',
      'contact number',
    ],
  },
  {
    key: 'email',
    label: 'Email',
    type: 'text',
    aliases: ['email', 'email id', 'mail', 'mail id'],
  },
  {
    key: 'password',
    label: 'Password',
    type: 'text',
    allowDefault: true,
    note: 'Leave unmapped to auto-generate a temporary password per row.',
    aliases: ['password', 'pwd', 'pin'],
  },
];

const SLIP_FIELDS = [
  {
    key: 'date',
    label: 'Date',
    type: 'date',
    required: true,
    aliases: [
      'date',
      'slip date',
      'transaction date',
      'bill date',
      'invoice date',
    ],
  },
  {
    key: 'staff',
    label: 'Staff / Cashier',
    type: 'text',
    required: true,
    aliases: [
      'staff',
      'cashier',
      'staff name',
      'cashier name',
      'attendant',
      'employee',
      'operator',
      'salesman',
    ],
  },
  {
    key: 'slip_number',
    label: 'Slip number',
    type: 'text',
    aliases: [
      'slip number',
      'slip no',
      'slip',
      'bill no',
      'bill number',
      'invoice no',
      'invoice number',
      'voucher no',
      'receipt no',
    ],
  },
  {
    key: 'customer',
    label: 'Credit customer',
    type: 'text',
    required: true,
    aliases: [
      'customer',
      'customer name',
      'credit customer',
      'party',
      'party name',
      'account name',
      'ledger name',
    ],
  },
  {
    key: 'customer_mobile',
    label: 'Customer mobile',
    type: 'mobile',
    aliases: [
      'customer mobile',
      'mobile',
      'mobile number',
      'phone',
      'phone number',
      'contact number',
    ],
  },
  {
    key: 'vehicle_number',
    label: 'Vehicle number',
    type: 'text',
    aliases: [
      'vehicle',
      'vehicle number',
      'vehicle no',
      'truck no',
      'registration number',
    ],
  },
  {
    key: 'product',
    label: 'Product',
    type: 'text',
    aliases: ['product', 'item', 'fuel', 'product name', 'item name'],
  },
  {
    key: 'price',
    label: 'Rate',
    type: 'number',
    aliases: ['price', 'rate', 'unit price', 'rate per litre', 'unit rate'],
  },
  {
    key: 'quantity',
    label: 'Quantity',
    type: 'number',
    aliases: ['quantity', 'qty', 'litres', 'liters', 'volume', 'ltrs'],
  },
  {
    key: 'amount',
    label: 'Amount',
    type: 'number',
    required: true,
    aliases: [
      'amount',
      'value',
      'total',
      'net amount',
      'taxable amount',
      'gross amount',
    ],
  },
  {
    key: 'discount',
    label: 'Discount',
    type: 'number',
    aliases: ['discount', 'disc', 'rebate'],
  },
  {
    key: 'tax',
    label: 'Tax group',
    type: 'text',
    aliases: ['tax', 'gst', 'tax group', 'tax name'],
  },
  {
    key: 'cash_given',
    label: 'Cash given',
    type: 'number',
    aliases: ['cash given', 'cash', 'cash paid', 'amount given'],
  },
  {
    key: 'notes',
    label: 'Notes',
    type: 'text',
    aliases: ['notes', 'remarks', 'narration', 'description', 'comments'],
  },
];

/**
 * Best-effort match of sheet headers against each target field. A header is
 * only ever claimed by one field, and fields are matched in declaration order
 * so the more specific ones win.
 */
const autoDetectMapping = (headers, fields) => {
  const mapping = {};
  const claimed = new Set();
  const normalizedHeaders = headers.map((header) => ({
    header,
    normalized: normalize(header),
  }));

  const pick = (predicate) =>
    normalizedHeaders.find(
      (item) =>
        !claimed.has(item.header) && item.normalized && predicate(item.normalized),
    );

  fields.forEach((field) => {
    const aliases = [field.label, ...field.aliases].map(normalize);
    const exact = pick((value) => aliases.includes(value));
    const partial =
      exact ||
      pick((value) =>
        aliases.some(
          (alias) =>
            alias.length > 3 &&
            (value.includes(alias) || alias.includes(value)),
        ),
      );
    const match = exact || partial;
    if (match) {
      claimed.add(match.header);
      mapping[field.key] = match.header;
    } else {
      mapping[field.key] = '';
    }
  });

  return mapping;
};

/** Applies a mapping (and any manual defaults) to a raw sheet row. */
const applyMapping = (row, fields, mapping, defaults = {}) => {
  const result = {};
  fields.forEach((field) => {
    const column = mapping[field.key];
    const raw = column ? row[column] : defaults[field.key];
    const value = parseByType(
      raw === undefined || raw === '' ? defaults[field.key] : raw,
      field.type,
    );
    result[field.key] = value;
  });
  return result;
};

/** Reads the first sheet-like structure out of an uploaded workbook. */
const readWorkbook = (arrayBuffer) => {
  const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
  return workbook;
};

const readSheet = (workbook, sheetName) => {
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) {
    return { headers: [], rows: [] };
  }
  const rows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: true });
  const headers = XLSX.utils
    .sheet_to_json(sheet, { header: 1, defval: '', raw: true })[0]
    ?.map((header) => String(header).trim())
    .filter((header) => header !== '');

  return {
    headers: headers || [],
    rows: rows.filter((row) =>
      Object.values(row).some((value) => parseText(value) !== ''),
    ),
  };
};

const currency = (value) =>
  Number.isFinite(Number(value)) ? Number(value).toFixed(2) : '0.00';

const generatePassword = () => Math.random().toString(36).slice(2, 10);

export {
  applyMapping,
  autoDetectMapping,
  currency,
  CUSTOMER_FIELDS,
  generatePassword,
  normalize,
  readSheet,
  readWorkbook,
  SLIP_FIELDS,
  splitFullName,
  STAFF_FIELDS,
};
